import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import BackButton from "../components/BackButton";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { clearActowizRequest, fetchActowizProducts } from "../store/actowiz/actowizSlice";
import { apiRequest } from "../api/client";

const PLATFORM_LABELS = {
  tesco: "Tesco",
  asda: "ASDA",
  morrison: "Morrisons",
  sainsbury: "Sainsbury's",
};

const FEATURED_PLATFORMS = new Set(["tesco", "asda", "morrison", "sainsbury"]);

function PlatformProducts({ onOpenAuth, onOpenMenu }) {
  const { platform } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const platformCatalog = useAppSelector((s) => s.actowiz.platformCatalog);
  const statusByPlatform = useAppSelector((s) => s.actowiz.statusByPlatform);
  const errorByPlatform = useAppSelector((s) => s.actowiz.errorByPlatform);

  const p = (platform || "").toLowerCase();
  const label = PLATFORM_LABELS[p] || "Products";

  const [page, setPage] = useState(1);
  const [pricingMode, setPricingMode] = useState("Base"); // UI-only for now
  const [categoryOpen, setCategoryOpen] = useState(true);
  const [subCategoryOpen, setSubCategoryOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(() => new Set());
  const [selectedSubcategories, setSelectedSubcategories] = useState(() => new Set());
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const offset = 12;

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  useEffect(() => {
    setPage(1);
  }, [p]);

  useEffect(() => {
    setSelectedCategories(new Set());
    setSelectedSubcategories(new Set());
  }, [p]);

  useEffect(() => {
    if (!p) return;
    const start = (page - 1) * offset;
    dispatch(
      fetchActowizProducts({
        platform: p,
        category: selectedCategories.size ? Array.from(selectedCategories) : undefined,
        sub_category: selectedSubcategories.size ? Array.from(selectedSubcategories) : undefined,
        start,
        offset,
        mode: "replace",
        force: true,
      })
    );
  }, [p, page, offset, dispatch, selectedCategories, selectedSubcategories]);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  const items = byPlatform?.[p] || [];
  const status = statusByPlatform?.[p] || "idle";
  const error = errorByPlatform?.[p] || "";
  const catalog = platformCatalog?.[p] || null;

  const hasPrev = page > 1;
  const hasNext = items.length === offset;
  const getRequestKey = (targetPage) => {
    const catKey = encodeURIComponent(
      Array.from(selectedCategories).join(",").trim().toLowerCase()
    );
    const subKey = encodeURIComponent(
      Array.from(selectedSubcategories).join(",").trim().toLowerCase()
    );
    return `${p || "all"}:${catKey}:${subKey}:${(targetPage - 1) * offset}:${offset}`;
  };

  const handleAddToList = useCallback(
    async (product) => {
      const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
      if (!minimalRes.ok) {
        const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
        throw new Error(errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists.");
      }
      const lists = Array.isArray(minimalRes.data)
        ? minimalRes.data
        : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];
      if (!lists.length) throw new Error("No grocery list found. Please create a list first.");

      let selectedListId = null;
      if (lists.length === 1) {
        selectedListId = lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
      } else {
        const selected = await chooseList(lists);
        if (!selected) {
          const cancelError = new Error("ADD_TO_LIST_CANCELLED");
          cancelError.code = "ADD_TO_LIST_CANCELLED";
          throw cancelError;
        }
        selectedListId = selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
      }
      if (!selectedListId) throw new Error("Unable to select grocery list.");

      const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
      if (!productId) throw new Error("Invalid product.");

      const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
        method: "POST",
        body: {
          product_id: String(productId),
          product_name: product?.title || product?.product_name || product?.name || product?.sku || "",
          platform: p || undefined,
          image_url: product?.images?.[0] || "",
          price: product?.price ?? null,
          weight: product?.packsize || product?.weight || "",
          quantity: 1,
        },
      });
      if (!addRes.ok) {
        const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
        throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
      }
    },
    [chooseList, p]
  );

  const showFeaturedUi = FEATURED_PLATFORMS.has(p);

  const categoryOptions = useMemo(() => {
    if (Array.isArray(catalog?.categories) && catalog.categories.length > 0) {
      return catalog.categories
        .map((c) => String(c?.category || "").trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    }
    const set = new Set();
    items.forEach((x) => {
      const v = String(x?.category || "").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const subcategoryOptions = useMemo(() => {
    if (Array.isArray(catalog?.categories) && catalog.categories.length > 0) {
      const subs = new Set();
      catalog.categories.forEach((c) => {
        const cat = String(c?.category || "").trim();
        if (selectedCategories.size > 0 && !selectedCategories.has(cat)) return;
        (Array.isArray(c?.subcategories) ? c.subcategories : []).forEach((sc) => {
          const v = String(sc || "").trim();
          if (v) subs.add(v);
        });
      });
      return Array.from(subs).sort((a, b) => a.localeCompare(b));
    }
    const set = new Set();
    items.forEach((x) => {
      const cat = String(x?.category || "").trim();
      if (selectedCategories.size > 0 && !selectedCategories.has(cat)) return;
      const v = String(x?.subcategory || "").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items, selectedCategories, catalog]);

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      {showFeaturedUi ? (
        <main className="mx-auto w-full max-w-8xl flex-1 px-4 py-6 md:px-6">
          <div className="mb-6 rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-sm flex items-end justify-between gap-4">
            <div>
              <BackButton className="mb-3" />
              <h1 className="text-[28px] font-bold text-[#24303A] md:text-[28px]">
                {label} Products
              </h1>
              <p className="mt-1 text-[14px] text-[#6E7B86] font-normal">
                Browse {label} Products.
              </p>
            </div>
            <div className="flex items-center gap-2 text-[14px]">
              <button
                type="button"
                disabled={!hasPrev || status === "loading"}
                onClick={() =>
                  setPage((v) => {
                    const nextPage = Math.max(1, v - 1);
                    dispatch(clearActowizRequest(getRequestKey(nextPage)));
                    return nextPage;
                  })
                }
                className="rounded-full border-2 shadow-sm border-[#E6EAF0] bg-[#FFFFFF1A] px-3 py-1.5 font-bold text-[#6E7B86] disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-[#6E7B86]">Page {page}</span>
              <button
                type="button"
                disabled={!hasNext || status === "loading"}
                onClick={() =>
                  setPage((v) => {
                    const nextPage = v + 1;
                    dispatch(clearActowizRequest(getRequestKey(nextPage)));
                    return nextPage;
                  })
                }
                className="rounded-full shadow-sm bg-[#7352C7] px-3 py-1.5 font-bold text-white disabled:opacity-50 shadow-sm"
              >
                Next
              </button>
            </div>
          </div>

          <div className="flex gap-6">
            <aside className="hidden w-64 rounded-2xl border border-[#E6EAF0] bg-white p-4 text-[14px] shadow-sm md:block">
              <h2 className="mb-4 pb-4 text-[20px] font-medium border-b-2 border-[#E6EAF0] text-[#24303A]">Filters</h2>

              <div className="space-y-4 ">
                <div className="bg-white border-b-2 border-[#E6EAF0] pb-2">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-1 py-2 text-left"
                    onClick={() => setCategoryOpen((v) => !v)}
                  >
                    <span className="text-[14px] font-medium text-[#24303A]">Category</span>
                    <span className="text-[#6E7B86]">{categoryOpen ? "▴" : "▾"}</span>
                  </button>
                  {categoryOpen && (
                    <div className="px-3 pb-3 space-y-2 text-[13px] text-[#6E7B86]">
                      {categoryOptions.length === 0 && (
                        <div className="text-[12px] text-slate-400">No categories</div>
                      )}
                      {categoryOptions.map((c) => (
                        <label key={c} className="flex items-center gap-2 text-[13px] font-normal text-[#6E7B86]">
                          <input
                            type="checkbox"
                            checked={selectedCategories.has(c)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setPage(1);
                              setSelectedCategories((prev) => {
                                const next = new Set(prev);
                                if (checked) next.add(c);
                                else next.delete(c);
                                return next;
                              });
                              setSelectedSubcategories(new Set());
                            }}
                            className="h-3.5 w-3.5 accent-[#7352C7]"
                            style={{ accentColor: "#7352C7" }}
                          />
                          <span>{c}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-1 py-2 text-left"
                    onClick={() => setSubCategoryOpen((v) => !v)}
                  >
                    <span className="text-[14px] font-medium text-[#24303A]">Sub Category</span>
                    <span className="text-[#6E7B86]">{subCategoryOpen ? "▴" : "▾"}</span>
                  </button>
                  {subCategoryOpen && (
                    <div className="px-3 pb-3 space-y-2 text-[13px] text-[#6E7B86]">
                      {subcategoryOptions.length === 0 && (
                        <div className="text-[12px] text-slate-400">No sub categories</div>
                      )}
                      {subcategoryOptions.map((sc) => (
                        <label key={sc} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedSubcategories.has(sc)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setPage(1);
                              setSelectedSubcategories((prev) => {
                                const next = new Set(prev);
                                if (checked) next.add(sc);
                                else next.delete(sc);
                                return next;
                              });
                            }}
                            className="h-3.5 w-3.5 accent-[#7352C7]"
                            style={{ accentColor: "#7352C7" }}
                          />
                          <span>{sc}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </aside>

            <section className="flex-1 space-y-6">
              <div className="flex flex-wrap gap-2">
                {["Base", "Loyalty + Offer"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setPricingMode(m)}
                    className={`rounded-full px-6 py-1.5 text-[14px] font-bold whitespace-nowrap shadow-sm ring-1 ring-[#E6EAF0] ${
                      pricingMode === m
                        ? "bg-[#7352C71A] text-[#7352C7] ring-[#7352C7]"
                        : "bg-white text-[#6E7B86]"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>

              {status === "loading" && items.length === 0 && (
                <div className="flex justify-center py-16">
                  <span className="text-[14px] text-slate-500">Loading…</span>
                </div>
              )}

              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700 whitespace-pre-line">
                  {error}
                </div>
              )}

              {!error && status !== "loading" && items.length === 0 && (
                <div className="flex justify-center py-16">
                  <span className="text-[14px] text-slate-500">No products found.</span>
                </div>
              )}

              {!error &&
                status !== "loading" &&
                items.length === 0 &&
                (selectedCategories.size > 0 || selectedSubcategories.size > 0) && (
                <div className="flex justify-center py-16">
                  <span className="text-[14px] text-slate-500">No products match your filters.</span>
                </div>
              )}

              {items.length > 0 && (
                <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
                  {items.map((product, idx) => (
                    <ProductCard
                      key={`${product.pid || product.sku}-${idx}`}
                      accent={["orange", "blue", "green", "purple"][idx % 4]}
                      product={product}
                      showComparison={false}
                      retailerLabel={PLATFORM_LABELS[p]}
                      onAddToList={handleAddToList}
                      onClick={() =>
                        navigate(
                          `/product-store/${encodeURIComponent(p)}/${encodeURIComponent(
                            product.pid || product.sku
                          )}`
                          ,
                          { state: { product } }
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>
      ) : (
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-10 md:px-6">
          <div className="mb-6 rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-sm flex items-end justify-between gap-4">
            <div>
              <BackButton className="mb-3" />
              <h1 className="text-2xl font-extrabold text-[#24303A] md:text-3xl">
                {label} Products
              </h1>
              <p className="mt-1 text-[14px] text-[#6E7B86]">
                Browse products from {label}.
              </p>
            </div>
            <div className="flex items-center gap-2 text-[14px]">
              <button
                type="button"
                disabled={!hasPrev || status === "loading"}
                onClick={() =>
                  setPage((v) => {
                    const nextPage = Math.max(1, v - 1);
                    dispatch(clearActowizRequest(getRequestKey(nextPage)));
                    return nextPage;
                  })
                }
                className="rounded-full border border-[#E6EAF0] bg-white px-3 py-1.5 font-semibold text-slate-700 disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-slate-500">Page {page}</span>
              <button
                type="button"
                disabled={!hasNext || status === "loading"}
                onClick={() =>
                  setPage((v) => {
                    const nextPage = v + 1;
                    dispatch(clearActowizRequest(getRequestKey(nextPage)));
                    return nextPage;
                  })
                }
                className="rounded-full bg-[#7352C7] px-3 py-1.5 font-semibold text-white disabled:opacity-50 shadow-sm"
              >
                Next
              </button>
            </div>
          </div>

          {status === "loading" && items.length === 0 && (
            <div className="flex justify-center py-16">
              <span className="text-[14px] text-slate-500">Loading…</span>
            </div>
          )}

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700 whitespace-pre-line">
              {error}
            </div>
          )}

          {!error && status !== "loading" && items.length === 0 && (
            <div className="flex justify-center py-16">
              <span className="text-[14px] text-slate-500">No products found.</span>
            </div>
          )}

          {items.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-5">
              {items.map((product, idx) => (
                <ProductCard
                  key={`${product.pid || product.sku}-${idx}`}
                  accent={["orange", "blue", "green", "purple"][idx % 4]}
                  product={product}
                  showComparison={false}
                  retailerLabel={PLATFORM_LABELS[p]}
                  onAddToList={handleAddToList}
                  onClick={() =>
                    navigate(
                      `/product-store/${encodeURIComponent(p)}/${encodeURIComponent(
                        product.pid || product.sku
                      )}`
                      ,
                      { state: { product } }
                    )
                  }
                />
              ))}
            </div>
          )}
        </main>
      )}

      <Footer />
      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-[14px] text-slate-500">Select the list where you want to add this product.</p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-[#E6EAF0] bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-[14px] font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-[#E6EAF0] bg-white px-3 py-1.5 text-[14px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlatformProducts;

